import type { ExportColumn } from '@/components/ui'
import type { ReorderAlertRow, VendorPORow } from '../api/types'
import { daysPastDue, isOverduePO } from './overdue'
import { shipStatusLabel } from './ship-status'

function _poStatus(r: VendorPORow): string {
  if (isOverduePO(r)) {
    const d = daysPastDue(r.expected_receipt)
    return d != null ? `Overdue ${d}d` : 'Overdue'
  }
  if (r.qty_received > 0 && r.qty_open > 0) return 'Partially received'
  if (r.qty_open === 0) return 'Closed'
  return 'Open'
}

/**
 * CSV export columns for the Open Vendor POs tab. Stable keys (never
 * rename — saved templates reference them by key).
 */
export const PROCUREMENT_PO_COLUMNS: readonly ExportColumn<VendorPORow>[] = [
  { key: 'po_number', header: 'PO #', value: (r) => r.po_number },
  { key: 'po_ln', header: 'PO:LN', value: (r) => r.po_ln },
  { key: 'vendor', header: 'Vendor', value: (r) => r.vendor },
  { key: 'po_date', header: 'PO Date', value: (r) => r.po_date },
  { key: 'po_confirmed', header: 'Confirmed', value: (r) => r.po_confirmed },
  { key: 'expected_receipt', header: 'Expected', value: (r) => r.expected_receipt },
  { key: 'revised_ship_date', header: 'Revised Ship Date', value: (r) => r.revised_ship_date },
  {
    key: 'ship_status',
    header: 'Ship Date Status',
    value: (r) => shipStatusLabel(r.ship_status ?? r.ship_confirmation_stage),
  },
  { key: 'estimate_count', header: 'Estimates Logged', value: (r) => r.estimate_count ?? 0 },
  { key: 'first_logged_estimate', header: 'First Estimate', value: (r) => r.first_logged_estimate ?? null },
  { key: 'estimate_slip_days', header: 'Estimate Slip (days)', value: (r) => r.estimate_slip_days ?? null },
  { key: 'line', header: 'Line', value: (r) => r.line },
  { key: 'product_name', header: 'Product', value: (r) => r.product_name },
  { key: 'uom', header: 'UoM', value: (r) => r.uom },
  { key: 'qty_ordered', header: 'Ordered', value: (r) => r.qty_ordered },
  { key: 'qty_received', header: 'Received', value: (r) => r.qty_received },
  { key: 'qty_open', header: 'Open', value: (r) => r.qty_open },
  { key: 'unit_cost', header: 'Unit cost', value: (r) => r.unit_cost },
  { key: 'line_total', header: 'Line total', value: (r) => r.line_total },
  { key: 'status', header: 'Status', value: (r) => _poStatus(r) },
] as const

/** CSV export columns for the Reorder Alerts tab. */
export const PROCUREMENT_ALERT_COLUMNS: readonly ExportColumn<ReorderAlertRow>[] = [
  { key: 'product', header: 'Product', value: (r) => r.product },
  { key: 'warehouse', header: 'Warehouse', value: (r) => r.warehouse },
  { key: 'qty_on_hand', header: 'On hand', value: (r) => r.qty_on_hand },
  { key: 'min_qty', header: 'Min', value: (r) => r.min_qty },
  { key: 'max_qty', header: 'Max', value: (r) => r.max_qty },
  { key: 'qty_forecast', header: 'Forecast', value: (r) => r.qty_forecast },
  { key: 'below_min', header: 'Below min', value: (r) => (r.below_min ? 'Yes' : 'No') },
] as const
